import React, {
  ChangeEvent,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useAtom } from 'jotai';
import { DateValue } from 'react-aria-components';

import {
  useAccountInfo,
  useFullPayoffAmount,
  useLoanSummary,
  useScheduledPayments,
} from '@consumer-frontend/nc-api';
import {
  getReactNodeText,
  isDefined,
  isInstallment,
  LOAN_STATUS,
  today,
} from '@consumer-frontend/nc-utils';

import { NcCustomAmount } from './NcCustomAmount';
import { NcFullPayoffAmount } from './NcFullPayoffAmount';
import { NcNextMinimumAmount } from './NcNextMinimumAmount';
import { NcPastDueAmount } from './NcPastDueAmount';
import {
  accountIdAtom,
  hasFetchErrorAtom,
} from '../../store';
import { NcAlert } from '../NcAlert';
import { NcRadioButton } from '../NcRadioButton';
import { NcRadioGroup } from '../NcRadioGroup';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/payment/amount.scss';

export type NcPaymentAmountPropsT = {
  disableFullPayoffOption: boolean;
  disableNextMinimumOption: boolean;
  updateValue: (value?: number) => void;
  errorMessage?: ReactNode;
  infoMessages?: ReactNode[];
  paymentDate?: DateValue;
  value?: number;
};
export type NcPaymentAmountT = React.FC<NcPaymentAmountPropsT>;

export const NcPaymentAmount: NcPaymentAmountT = ({
  disableFullPayoffOption,
  disableNextMinimumOption,
  errorMessage,
  infoMessages,
  paymentDate,
  updateValue,
  value,
}) => {
  // Fetch data
  const [accountId] = useAtom(accountIdAtom);
  const [hasFetchError, setHasFetchError] = useAtom(hasFetchErrorAtom);
  const { accountInfo: { loanNumber, loanType } = {} } = useAccountInfo(accountId);

  const {
    error: loanSummaryError,
    isLoading: isLoanSummaryLoading,
    loanSummary: {
      loanStatus,
      pastDueBalance = 0,
      nextPayment,
    } = {},
  } = useLoanSummary(accountId, loanNumber);
  const {
    amountDue = 0,
    dueDate,
    originalAmountDue = 0,
  } = nextPayment || {};

  const {
    error: scheduledPaymentsError,
    isLoading: areScheduledPaymentsLoading,
    nextObligationalScheduledPayment: { isAutopay, isPaymentPending } = {},
    scheduledPayments,
  } = useScheduledPayments({ accountId, loanNumber });

  // Loan state
  const isCalledDue = loanStatus === LOAN_STATUS.CALLED_DUE;
  const isPastDue = loanStatus === LOAN_STATUS.PAST_DUE;

  const { amount: fullPayoffAmount, error: fullPayoffAmountError } = useFullPayoffAmount({
    accountId,
    loanNumber,
    paymentDate: isCalledDue ? today() : paymentDate,
  });

  // View helpers
  const isLoading = isLoanSummaryLoading || areScheduledPaymentsLoading;
  const showAutoPayBadges = scheduledPayments?.every((payment) => dueDate !== payment.dueDate || payment.isAutopay);
  const isInstallmentAmountZero = isInstallment(loanType) && amountDue === 0;
  const showNextMinimumOption = !isCalledDue && !isPastDue && nextPayment && !isInstallmentAmountZero;

  // Local state
  const [isCustomAmount, setIsCustomAmount] = useState<boolean>(false);
  const showInfoMessages = useMemo(
    () => isDefined(infoMessages) && !!infoMessages.length,
    [infoMessages],
  );

  // Send fetch errors to global state for NcMakeAPayment to handle
  useEffect(
    () => {
      setHasFetchError((prev) => prev || !!(
        loanSummaryError
        || scheduledPaymentsError
        || fullPayoffAmountError
      ));
    },
    [
      fullPayoffAmountError,
      loanSummaryError,
      scheduledPaymentsError,
      setHasFetchError,
    ],
  );

  // Set custom amount if value is defined but not one of the preset options
  useEffect(
    () => {
      const shouldBeCustomAmount = !!value && ![
        isPastDue ? pastDueBalance : undefined,
        (isPaymentPending || disableNextMinimumOption) ? undefined : amountDue,
        fullPayoffAmount,
      ].includes(value);

      setIsCustomAmount(shouldBeCustomAmount);
    },
    [
      amountDue,
      disableNextMinimumOption,
      fullPayoffAmount,
      isPastDue,
      isPaymentPending,
      pastDueBalance,
      value,
    ],
  );

  // Change handlers
  const onChangeAmount = (event: ChangeEvent<HTMLInputElement>) => {
    const { id, value: newValue } = event.target;

    setIsCustomAmount(() => id === 'payment-amount--custom');
    updateValue(Number.isNaN(+newValue) ? undefined : +newValue);
  };

  const onSaveCustomAmount = (amount: number) => {
    setIsCustomAmount(true);
    updateValue(amount);
  };

  return !hasFetchError && !isLoading && (
    <NcRadioGroup
      className="nc-payment-amount"
      controlId="payment-amount"
      groupError
      errorMessage={errorMessage}
      label="Payment Amount"
      value={value}
    >
      {/* PAST DUE */}
      {!isCalledDue && isPastDue && (
        <NcRadioButton
          id="payment-amount--past-due"
          label={<NcPastDueAmount amount={pastDueBalance} />}
          checked={value === pastDueBalance && !isCustomAmount}
          errorMessage={errorMessage}
          groupError
          onChange={onChangeAmount}
          value={pastDueBalance}
        />
      )}

      {/* NEXT/MINIMUM */}
      {showNextMinimumOption && (
        <NcRadioButton
          id="payment-amount--next-minimum"
          label={
            <NcNextMinimumAmount
              amountDue={amountDue}
              dueDate={dueDate}
              isAutopay={isAutopay}
              isPaymentPending={isPaymentPending}
              isReplacement={isAutopay && value === amountDue}
              loanType={loanType}
              originalAmountDue={originalAmountDue}
              showAutoPayBadges={showAutoPayBadges}
            />
          }
          checked={value === amountDue && !isCustomAmount}
          errorMessage={errorMessage}
          groupError
          onChange={onChangeAmount}
          unavailable={isPaymentPending || amountDue === 0 || disableNextMinimumOption}
          value={amountDue}
        />
      )}

      {/* FULL PAYOFF */}
      <NcRadioButton
        id="payment-amount--full-payoff"
        label={
          <NcFullPayoffAmount
            amount={fullPayoffAmount}
            isCalledDue={isCalledDue}
            isDisabled={disableFullPayoffOption}
          />
        }
        checked={
          isDefined(fullPayoffAmount)
          && value === fullPayoffAmount
          && !isCustomAmount
        }
        errorMessage={errorMessage}
        groupError
        onChange={onChangeAmount}
        unavailable={!isDefined(fullPayoffAmount) || disableFullPayoffOption}
        value={fullPayoffAmount}
      />

      {/* CUSTOM */}
      <NcCustomAmount
        errorMessage={errorMessage}
        isChecked={isCustomAmount}
        onSave={onSaveCustomAmount}
        value={isCustomAmount ? value : undefined}
      />

      {showInfoMessages && (
        <NcAlert
          chromaticIgnore
          className="payment-amount-messaging"
          dismissible={false}
          variant="info"
        >
          {infoMessages?.map(((message) => (
            <div className="info-message" key={getReactNodeText(message)}>{message}</div>
          )))}
        </NcAlert>
      )}
    </NcRadioGroup>
  );
};
